(() => {
  const SELECTOR = '[data-runix-variant]';

  const parseOptions = (input) => {
    try {
      return JSON.parse(input.dataset.options || '[]');
    } catch (_error) {
      return [];
    }
  };

  const applyVariant = (root, input, broadcast = true) => {
    if (!root || !input) return;

    const available = input.dataset.available === 'true';
    const hiddenInput = root.querySelector('input[name="id"]');
    const image = root.querySelector('[data-runix-image]');
    const price = root.querySelector('[data-runix-price]');
    const compare = root.querySelector('[data-runix-compare]');
    const selectedTitle = root.querySelector('[data-runix-selected-title]');
    const submit = root.querySelector('[name="add"]');
    const submitText = submit?.querySelector('[data-runix-submit-text]');

    if (hiddenInput) {
      hiddenInput.value = input.value;
      hiddenInput.disabled = !available;
    }
    if (image && input.dataset.image) {
      image.removeAttribute('srcset');
      image.src = input.dataset.image;
      image.alt = input.dataset.imageAlt || image.alt;
    }
    if (price) price.textContent = input.dataset.price || '';
    if (compare) {
      compare.textContent = input.dataset.compare || '';
      compare.hidden = !input.dataset.compare;
    }
    if (selectedTitle) selectedTitle.textContent = input.dataset.title || '';
    if (submit) submit.disabled = !available;
    if (submitText) submitText.textContent = available ? 'Add to Cart' : 'Sold Out';

    if (broadcast) {
      document.dispatchEvent(new CustomEvent('runix:variant-change', {
        detail: {
          productId: root.dataset.productId,
          variantId: input.value,
          options: parseOptions(input),
          source: root,
        },
      }));
    }
  };

  const syncVariant = (root, variantId) => {
    const input = Array.from(root.querySelectorAll(SELECTOR)).find((item) => item.value === String(variantId));
    if (!input) return;
    input.checked = true;
    applyVariant(root, input, false);
  };

  class RunixPurchase extends HTMLElement {
    connectedCallback() {
      this.addEventListener('change', this.onChange);
      document.addEventListener('runix:variant-change', this.onExternalChange);
      applyVariant(this, this.querySelector(`${SELECTOR}:checked`) || this.querySelector(SELECTOR), false);
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('runix:variant-change', this.onExternalChange);
    }

    onChange = (event) => {
      if (event.target.matches(SELECTOR)) applyVariant(this, event.target);
    };

    onExternalChange = (event) => {
      if (event.detail?.source === this || String(event.detail?.productId) !== String(this.dataset.productId)) return;
      syncVariant(this, event.detail.variantId);
    };
  }

  class RunixSticky extends HTMLElement {
    connectedCallback() {
      this.updateOffset();
      this.resizeObserver = new ResizeObserver(() => this.updateOffset());
      this.resizeObserver.observe(this);
      this.purchaseButton = document.querySelector('product-info [name="add"]');
      if (this.purchaseButton) {
        this.visibilityObserver = new IntersectionObserver(([entry]) => {
          this.setVisibility(!entry.isIntersecting && entry.boundingClientRect.bottom < 0);
        });
        this.visibilityObserver.observe(this.purchaseButton);
      } else {
        this.setVisibility(true);
      }
      this.addEventListener('change', this.onChange);
      document.addEventListener('runix:variant-change', this.onExternalChange);
      applyVariant(this, this.querySelector(`${SELECTOR}:checked`) || this.querySelector(SELECTOR), false);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const variant = event?.data?.variant;
          if (variant?.id) syncVariant(this, variant.id);
        });
      }
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.visibilityObserver?.disconnect();
      this.unsubscribeVariant?.();
      this.removeEventListener('change', this.onChange);
      document.removeEventListener('runix:variant-change', this.onExternalChange);
      if (!document.querySelector('runix-sticky')) document.documentElement.classList.remove('runix-sticky-visible');
    }

    updateOffset() {
      document.documentElement.style.setProperty('--runix-sticky-height', `${this.offsetHeight}px`);
    }

    setVisibility(visible) {
      this.classList.toggle('is-visible', visible);
      document.documentElement.classList.toggle('runix-sticky-visible', visible);
    }

    onChange = (event) => {
      if (!event.target.matches(SELECTOR)) return;
      applyVariant(this, event.target);
      this.syncNativeProduct(parseOptions(event.target));
    };

    onExternalChange = (event) => {
      if (event.detail?.source === this || String(event.detail?.productId) !== String(this.dataset.productId)) return;
      syncVariant(this, event.detail.variantId);
    };

    syncNativeProduct(options) {
      const productInfo = document.querySelector('product-info');
      if (!productInfo || !options.length) return;

      const fieldsets = productInfo.querySelectorAll('variant-selects fieldset');
      options.forEach((value, index) => {
        const fieldset = fieldsets[index];
        if (!fieldset) return;
        const input = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find((item) => item.value === value);
        if (input && !input.checked) input.click();
      });
    }
  }

  class RunixFaq extends HTMLElement {
    connectedCallback() {
      this.details = Array.from(this.querySelectorAll('details'));
      this.details.forEach((item) => {
        this.updateExpanded(item);
        item.addEventListener('toggle', this.onToggle);
      });
    }

    disconnectedCallback() {
      this.details?.forEach((item) => item.removeEventListener('toggle', this.onToggle));
    }

    onToggle = (event) => this.updateExpanded(event.currentTarget);

    updateExpanded(item) {
      item.querySelector('summary')?.setAttribute('aria-expanded', String(item.open));
    }
  }

  if (!customElements.get('runix-purchase')) customElements.define('runix-purchase', RunixPurchase);
  if (!customElements.get('runix-sticky')) customElements.define('runix-sticky', RunixSticky);
  if (!customElements.get('runix-faq')) customElements.define('runix-faq', RunixFaq);
})();
